import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '#store/api';
import { DataApiResponse } from './types';

export const AFTER_ACTION_REPORT_SETTINGS_KEY = 'afterActionReportSettings';

export interface AfterActionReportSettingsBase {
  reportName: string;
  executiveSummary?: string;
  participatingOrganizations?: string;
  incidentId: string;
}

export interface AfterActionReportSettings extends AfterActionReportSettingsBase {
  id: string;
}

interface CreateAfterActionReportSettingsParams {
  settings: AfterActionReportSettingsBase;
}

interface UpdateAfterActionReportSettingsParams {
  id: string;
  settings: AfterActionReportSettingsBase;
}

export interface GetAfterActionReportSettingsByIdParams {
  id: string;
}

export interface GetAfterActionReportSettingsByIncidentParams {
  incidentId: string;
}

export const useAfterActionReportSettingsById = (params: GetAfterActionReportSettingsByIdParams) => {
  return useQuery({
    queryKey: [AFTER_ACTION_REPORT_SETTINGS_KEY, params],
    queryFn: async () => {
      const response = await apis.afterActionReportSettings.getAfterActionReportSettingsById({ data: params });
      return response.data as DataApiResponse<AfterActionReportSettings>;
    },
  });
};

export const useCreateAfterActionReportSettings = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<any, Error, CreateAfterActionReportSettingsParams>({
    mutationFn: async (params: CreateAfterActionReportSettingsParams) => {
      const response = await apis.afterActionReportSettings.createAfterActionReportSettings({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AFTER_ACTION_REPORT_SETTINGS_KEY] });
    },
  });

  return {
    ...mutation,
    isLoading: mutation.status === 'pending',
  };
};

export const useUpdateAfterActionReportSettings = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: UpdateAfterActionReportSettingsParams) => {
      const response = await apis.afterActionReportSettings.updateAfterActionReportSettings({ data: params });
      return response.data as DataApiResponse<AfterActionReportSettings>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AFTER_ACTION_REPORT_SETTINGS_KEY] });
    },
  });

  return {
    ...mutation,
    isLoading: mutation.status === 'pending',
  };
};

export const useAfterActionReportSettingsByIncidentId = (params: GetAfterActionReportSettingsByIncidentParams) => {
  return useQuery({
    queryKey: [AFTER_ACTION_REPORT_SETTINGS_KEY, 'byIncidentId', params],
    queryFn: async () => {
      const response = await apis.afterActionReportSettings.getAfterActionReportSettingsByIncidentId({ data: params });
      return response.data as DataApiResponse<AfterActionReportSettings>;
    },
    enabled: !!params.incidentId,
  });
};
